import { useEffect, useMemo } from 'react';
// eslint-disable-next-line jira/import-whitelist
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { getProjectKeyId } from '@jira/router-resources__utils/src/getProjectKeyId.tsx';
import type {
	IrremovableField,
	IrremovableFieldsConfig,
} from '@jira/jql-builder__basic/src/utils/irremovable-fields/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { isHttpClientErrorResponse } from '@atlassian/jira-fetch/src/utils/is-error.tsx';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import {
	createResource,
	useResource,
	type RouterContext,
	type Match,
	type Query,
	type RouteResource,
} from '@atlassian/react-resource-router';

import type {
	FiltersProps,
	FetchFilterFieldsResult,
	RequestTypeFieldResponse,
} from './RequestTypeFieldResponse.types.tsx';
import { QUERY } from './Query.tsx';

const PACKAGE_NAME = 'jiraRouterResourcesServicedeskProjectSummary';
const TEAM_NAME = 'jsd-shield';
const RESOURCE_NAME = 'projectSummaryFilterFieldResource';
export const RESOURCE_TYPE = 'RESOURCE_TYPE_JSM_PROJECT_SUMMARY_FILTERS';

// oxlint-disable-next-line typescript/consistent-type-assertions
const createdField = {
	displayName: 'Created',
	fieldType: 'created',
	jqlTerm: 'created',
	operators: [
		'LESS_THAN_OR_EQUAL',
		'GREATER_THAN_OR_EQUAL',
		'NOT_IN',
		'IS_NOT',
		'LESS_THAN',
		'GREATER_THAN',
		'EQUALS',
		'IS',
		'IN',
		'NOT_EQUALS',
	],
	searchTemplate: 'created',
	type: 'created',
} as unknown as IrremovableField;

// oxlint-disable-next-line typescript/consistent-type-assertions
const priorityField = {
	displayName: 'Priority',
	fieldType: 'priority',
	jqlTerm: 'priority',
	operators: [
		'GREATER_THAN_OR_EQUAL',
		'LESS_THAN',
		'GREATER_THAN',
		'WAS_NOT',
		'IS',
		'IN',
		'CHANGED',
		'LESS_THAN_OR_EQUAL',
		'NOT_IN',
		'IS_NOT',
		'WAS_NOT_IN',
		'EQUALS',
		'WAS_IN',
		'WAS',
		'NOT_EQUALS',
	],
	searchTemplate: 'priority',
	type: 'priority',
} as unknown as IrremovableField;

const reportError = ({ error, id }: { error: Error; id?: string }) =>
	fireErrorAnalytics({
		error,
		sendToPrivacyUnsafeSplunk: true,
		meta: {
			id: id ? `${RESOURCE_NAME}.${id}` : RESOURCE_NAME,
			packageName: PACKAGE_NAME,
			teamName: TEAM_NAME,
		},
	});

const fetchFilterFields = async (cloudId: string): Promise<FetchFilterFieldsResult> => {
	try {
		const requestTypeFieldResponse = await performPostRequest<RequestTypeFieldResponse>(
			'/gateway/api/graphql',
			{
				body: JSON.stringify({
					query: QUERY,
					variables: {
						cloudId,
					},
				}),
				headers: {
					'content-type': 'application/json',
				},
			},
		);

		const requestTypeField = requestTypeFieldResponse.data?.jira?.requestTypeField;
		if (!requestTypeField) {
			return {
				requestTypeField: undefined,
			};
		}

		return {
			requestTypeField: {
				...requestTypeField,
				searchTemplate: requestTypeField.searchTemplate?.key,
				fieldType: requestTypeField.type,
				displayName: requestTypeField.displayName,
			},
		};
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		if (!isHttpClientErrorResponse(error)) {
			reportError({
				error,
				id: 'fetchFields',
			});
		}

		return {
			requestTypeField: undefined,
		};
	}
};

const getProjectKeyOrId = (match: Match, query: Query) => {
	const { projectKey, projectId } = getProjectKeyId(match, query);
	return projectKey || projectId;
};

const filterFieldsResource = createResource({
	type: RESOURCE_TYPE,
	getKey: ({ match, query }: RouterContext) =>
		fg('jsm_project_summary_filter_resource_cloud_scoped_cache_key')
			? RESOURCE_TYPE
			: RESOURCE_TYPE + getProjectKeyOrId(match, query),
	getData: (_, { tenantContext: { cloudId } }) => fetchFilterFields(cloudId),
	maxAge: Infinity,
});

export const getProjectSummaryFilterFieldsResource = (): RouteResource<FetchFilterFieldsResult> =>
	filterFieldsResource;

export const useGetProjectSummaryFilterFields = (): (({
	requestTypeField,
	requestTypeDisplayName,
	priorityDisplayName,
	createdDisplayName,
}: FiltersProps) => IrremovableFieldsConfig) => {
	const { data: filterResource } = useResource(filterFieldsResource);

	const useFilterFields = useMemo(
		() =>
			({
				requestTypeField = filterResource?.requestTypeField,
				requestTypeDisplayName,
				priorityDisplayName,
				createdDisplayName,
			}: FiltersProps): IrremovableFieldsConfig => {
				return [
					...(requestTypeField
						? [
								{
									...requestTypeField,
									...(requestTypeDisplayName && { displayName: requestTypeDisplayName }),
								},
							]
						: []),
					'status',
					'assignee',
					{
						...priorityField,
						...(priorityDisplayName && { displayName: priorityDisplayName }),
					},
					{ ...createdField, ...(createdDisplayName && { displayName: createdDisplayName }) },
				];
			},
		[filterResource],
	);

	return useFilterFields;
};

export const useRequestTypeJqlTerm = ({ jqlTerm }: { jqlTerm?: string }): string | undefined => {
	const { data: filterResource, loading } = useResource(filterFieldsResource);

	const term = jqlTerm ?? filterResource?.requestTypeField?.jqlTerm;
	const hasError = !loading && !term;

	useEffect(() => {
		if (hasError) {
			reportError({
				error: new Error('Error while getting request type jql term.'),
				id: 'rtJqlTerm',
			});
		}
	}, [hasError]);

	return term;
};
